import { toCompilableQuery } from '@powersync/drizzle-driver';
import { useQuery } from '@powersync/tanstack-react-query';
import { useQuery as useRestQuery } from '@tanstack/react-query';
import { count, eq } from 'drizzle-orm';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { boat_details } from '../powersync/schemas/boats.schema';
import { subscriptions } from '../powersync/schemas/subscriptions.schema';
import { useSystem } from '../powersync/system';
import { useActiveOrg } from '../providers/useActiveOrg';
import { useTypedOpenApi } from '../providers/useTypedOpenApi';
import { FREE_ORG_PLAN, hasRoomFor, type OrgEntitlement, type OrgLimit, type OrgPlan, resolveOrgPlan } from './orgPlan';
import { fetchServerOrgPlan, withServerAllowance } from './serverPlan';

/** The `by_org_core` tier in `sync-streams.yaml`, which carries `subscriptions`. */
const CORE_SYNC_PRIORITY = 1;

/** `boat_details.enabled` of a boat that is switched on. */
const ENABLED = 1;

/**
 * How much of each limit the organization uses. `null` is "not known here", never zero.
 *
 * Boats are counted on the device, only the ones switched on. Storage and AI queries come from the backend, so they stay
 * `null` offline. Members come from Clerk (`organization.membersCount`), which this package can't
 * import, so the app reads them itself.
 */
export type OrgUsage = Readonly<Record<OrgLimit, number | null>>;

export type OrgPlanState = {
  /** Free until `isLoaded`. Don't gate on it directly; use `hasEntitlement` and `limitFor`. */
  plan: OrgPlan;
  /** The org's subscriptions have synced at least once, or a row is already on the device. */
  isLoaded: boolean;
  usage: OrgUsage;
  /** When this month's AI queries start again from zero. `null` until the backend has answered. */
  aiQueriesResetAt: string | null;
  /** Whether the plan includes `entitlement`. True until the plan has loaded. */
  hasEntitlement: (entitlement: OrgEntitlement) => boolean;
  /** The plan's limit for `resource`, `null` for unlimited. Unlimited until the plan has loaded. */
  limitFor: (resource: OrgLimit) => number | null;
  /**
   * Whether one more of `resource` fits the plan, counted against `usage`. True until both the plan
   * and the usage are known, so members, which this hook doesn't count, always answer true here: a
   * caller that knows the member count uses `hasRoomFor(limitFor('users'), count)` instead.
   */
  hasRoom: (resource: OrgLimit) => boolean;
};

/**
 * Whether the sync tier at `priority` has completed at least once since this database was
 * connected. An org switch clears the database, so this goes false again until the new org's first
 * checkpoint lands.
 */
const useHasSyncedPriority = (priority: number): boolean => {
  const { powersync } = useSystem();

  const subscribe = useCallback(
    (onChange: () => void) => powersync.registerListener({ statusChanged: onChange }),
    [powersync],
  );
  const getSnapshot = useCallback(
    () => powersync.currentStatus.statusForPriority(priority)?.hasSynced ?? false,
    [powersync, priority],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

/** `GET /organization/plan`, per organization and per synced plan. */
export const serverOrgPlanKey = (clerkOrgId: string | null, plan: OrgPlan) =>
  ['organization-plan', clerkOrgId, plan.slug, plan.stripeStatus, plan.subscriptionId] as const;

/**
 * The active organization's plan, limits and usage.
 *
 * The plan comes from the synced `subscriptions` rows, so every member has it, offline too. Its
 * limits and entitlements, and the storage and AI queries used, come from `GET /organization/plan`
 * once it answers: those are what the backend enforces. Until then, and offline, the limits are
 * the MDC-4517 table's. The synced plan is part of the query's key, so a plan change that syncs in
 * asks the backend again rather than pairing the new plan with the old one's limits.
 *
 * Gates stay open until the plan has loaded. A new device, or one that just switched org, has no
 * rows for a moment, and reading that as Free would lock a paying organization out of its own data.
 * The backend enforces the real limit either way, so this is only about what the app shows.
 */
export const useOrgPlan = (): OrgPlanState => {
  const { drizzleDb } = useSystem();
  const db = drizzleDb();
  const hasSyncedCore = useHasSyncedPriority(CORE_SYNC_PRIORITY);

  const subscriptionsQuery = db
    .select({
      id: subscriptions.id,
      plan: subscriptions.plan,
      quantity: subscriptions.quantity,
      stripe_status: subscriptions.stripe_status,
      ends_at: subscriptions.ends_at,
      trial_ends_at: subscriptions.trial_ends_at,
      next_billing_date: subscriptions.next_billing_date,
    })
    .from(subscriptions);
  const { data: rows } = useQuery({
    queryKey: ['orgPlanSubscriptions'],
    query: toCompilableQuery(subscriptionsQuery),
  });

  // Only boats that are on take a slot, as in `usage.boats` of `GET /organization/plan` (MDC-4572).
  // A boat that is off stays on the device, and a new boat with no room is created off, not refused.
  const boatsQuery = db.select({ total: count() }).from(boat_details).where(eq(boat_details.enabled, ENABLED));
  const { data: boatCount } = useQuery({
    queryKey: ['orgPlanBoatCount'],
    query: toCompilableQuery(boatsQuery),
  });

  const isLoaded = rows !== undefined && (rows.length > 0 || hasSyncedCore);
  const syncedPlan = useMemo(() => (isLoaded && rows ? resolveOrgPlan(rows) : FREE_ORG_PLAN), [isLoaded, rows]);
  const boats = boatCount?.at(0)?.total ?? null;

  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();
  const { data: server } = useRestQuery({
    queryKey: serverOrgPlanKey(clerkOrgId, syncedPlan),
    queryFn: () => fetchServerOrgPlan(apiClient),
    enabled: isLoaded && clerkOrgId !== null,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const plan = useMemo(() => (server ? withServerAllowance(syncedPlan, server) : syncedPlan), [server, syncedPlan]);

  const usage = useMemo<OrgUsage>(
    () => ({
      boats,
      users: null,
      storageGb: server?.usage.storageGb ?? null,
      aiQueriesPerMonth: server?.usage.aiQueries ?? null,
      theBoatDbCompares: null,
    }),
    [boats, server],
  );
  const aiQueriesResetAt = server?.usage.aiQueriesResetAt ?? null;

  const hasEntitlement = useCallback(
    (entitlement: OrgEntitlement) => !isLoaded || plan.entitlements.has(entitlement),
    [isLoaded, plan],
  );
  const limitFor = useCallback((resource: OrgLimit) => (isLoaded ? plan.limits[resource] : null), [isLoaded, plan]);
  const hasRoom = useCallback(
    (resource: OrgLimit) => {
      const used = usage[resource];

      return !isLoaded || used === null || hasRoomFor(plan.limits[resource], used);
    },
    [isLoaded, plan, usage],
  );

  return { plan, isLoaded, usage, aiQueriesResetAt, hasEntitlement, limitFor, hasRoom };
};
